import { resolvePublicAssetUrl, socialIcon } from "../../../lib/assets"

/** Map report table platform labels to raster-friendly icons (PNG preferred for PDF). */
export function platformLabelToIconPath(label) {
  const text = String(label ?? "").toLowerCase()
  if (!text) return null
  if (text.includes("instagram")) return socialIcon("instagram.svg")
  if (text.includes("facebook")) return socialIcon("facebook.svg")
  if (text.includes("linkedin")) return socialIcon("linkedin.svg")
  if (text.includes("tiktok")) return socialIcon("tiktok.png")
  if (text.includes("twitter") || text === "x" || text.startsWith("x ")) return socialIcon("x.png")
  return null
}

export function reportPlatformIconUrl(label) {
  const path = platformLabelToIconPath(label)
  return path ? resolvePublicAssetUrl(path) : null
}

const PLATFORM_ICON_LABELS = [
  "Instagram",
  "Facebook",
  "Twitter/X",
  "LinkedIn",
  "TikTok",
  "X",
]

/** Absolute URL → { dataUrl, format } for PDF / html2canvas. */
const platformIconRasterCache = new Map()

function jsPdfFormatFromDataUrl(dataUrl) {
  if (typeof dataUrl !== "string") return null
  const match = /^data:image\/([\w+.-]+)/i.exec(dataUrl)
  if (!match) return null
  const subtype = match[1].toLowerCase()
  if (subtype === "png") return "PNG"
  if (subtype === "jpeg" || subtype === "jpg") return "JPEG"
  if (subtype === "webp") return "WEBP"
  return null
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function rasterizeUrlToPngDataUrl(url, size = 32) {
  const blob = await fetch(url, { mode: "same-origin", credentials: "omit", cache: "force-cache" }).then(
    (r) => (r.ok ? r.blob() : null),
  )
  if (!blob) return null
  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = objectUrl
    })
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    if (!ctx) return null
    ctx.clearRect(0, 0, size, size)
    ctx.drawImage(img, 0, 0, size, size)
    return canvas.toDataURL("image/png")
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/** Raster platform icons for PDF tables and SVG chart logos; SVG is converted to PNG. */
export async function fetchReportPlatformIconRaster(iconUrl) {
  if (!iconUrl) return null
  if (platformIconRasterCache.has(iconUrl)) {
    return platformIconRasterCache.get(iconUrl)
  }
  try {
    const sameOrigin =
      typeof window !== "undefined" &&
      (iconUrl.startsWith("/") || iconUrl.startsWith(window.location.origin))
    const response = await fetch(iconUrl, {
      mode: sameOrigin ? "same-origin" : "cors",
      credentials: "omit",
      cache: "force-cache",
    })
    if (!response.ok) {
      platformIconRasterCache.set(iconUrl, null)
      return null
    }
    const blob = await response.blob()
    if (!blob || blob.size < 8) {
      platformIconRasterCache.set(iconUrl, null)
      return null
    }
    let dataUrl = await blobToDataUrl(blob)
    let format = jsPdfFormatFromDataUrl(dataUrl)
    if (!format) {
      const png = await rasterizeUrlToPngDataUrl(iconUrl, 32)
      if (!png) {
        platformIconRasterCache.set(iconUrl, null)
        return null
      }
      dataUrl = png
      format = "PNG"
    }
    const raster = { dataUrl, format }
    platformIconRasterCache.set(iconUrl, raster)
    return raster
  } catch {
    platformIconRasterCache.set(iconUrl, null)
    return null
  }
}

export async function preloadReportPlatformIconRasters() {
  const urls = new Set()
  PLATFORM_ICON_LABELS.forEach((label) => {
    const url = reportPlatformIconUrl(label)
    if (url) urls.add(url)
  })
  ;["instagram.svg", "facebook.svg", "linkedin.svg", "tiktok.png", "x.png"].forEach((file) => {
    urls.add(resolvePublicAssetUrl(socialIcon(file)))
  })
  await Promise.all([...urls].map((url) => fetchReportPlatformIconRaster(url)))
}

function resolveCachedRaster(url) {
  if (!url) return null
  const absolute =
    url.startsWith("http") || url.startsWith("//")
      ? url
      : resolvePublicAssetUrl(url.startsWith("/") ? url : `/${url}`)
  const cached = platformIconRasterCache.get(absolute)
  if (cached?.dataUrl) return cached.dataUrl
  const pathOnly = platformIconRasterCache.get(url)
  if (pathOnly?.dataUrl) return pathOnly.dataUrl
  return null
}

/** Inline rasterized icons into DOM/SVG clones for html2canvas (bar logos + report tables). */
export function applyPlatformIconRastersToRoot(root) {
  if (!root) return
  root.querySelectorAll("img.report-platform-icon, img").forEach((img) => {
    const src = img.getAttribute("src")
    if (!src || src.startsWith("data:")) return
    const dataUrl = resolveCachedRaster(src)
    if (dataUrl) img.setAttribute("src", dataUrl)
  })
  root.querySelectorAll("image").forEach((node) => {
    const href =
      node.getAttribute("href") ||
      node.getAttributeNS("http://www.w3.org/1999/xlink", "href") ||
      ""
    if (!href || href.startsWith("data:")) return
    const dataUrl = resolveCachedRaster(href)
    if (dataUrl) {
      node.setAttribute("href", dataUrl)
      node.setAttributeNS("http://www.w3.org/1999/xlink", "href", dataUrl)
    }
  })
}
